import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";
import * as s from "../../FilterBar.style.css";
import type { MultiselectQuickFilterConfig } from "../../FilterBar.types";
import type { QuickFilterVariant } from "../QuickFilterControl";

export interface MultiselectFilterProps {
  config: MultiselectQuickFilterConfig;
  value: unknown;
  onChange: (v: unknown) => void;
  variant?: QuickFilterVariant;
}

export function MultiselectFilter({ config, value, onChange, variant = "bar" }: MultiselectFilterProps) {
  const isModal = variant === "modal";
  const selected = (Array.isArray(value) ? value : []) as string[];
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggle = (optValue: string) => {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange(next.length > 0 ? next : undefined);
  };

  const updatePosition = () => {
    const trigger = triggerRef.current;
    if (!trigger || !open) return;
    const rect = trigger.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
    });
  };

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const run = () => requestAnimationFrame(updatePosition);
    run();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [open]);

  const displayText =
    selected.length === 0
      ? "All"
      : selected.length === 1
        ? config.options.find((o) => o.value === selected[0])?.label ?? "1 selected"
        : `${selected.length} selected`;

  const dropdown = open && (
    <div
      ref={dropdownRef}
      className={s.quickFilterMultiselectDropdown}
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: "hidden" as const }
      }
      role="listbox"
      aria-multiselectable
    >
      {config.options.map((opt) => {
        const isSelected = selected.includes(opt.value);
        return (
          <div
            key={opt.value}
            className={s.quickFilterMultiselectOption}
            role="option"
            aria-selected={isSelected}
            onClick={() => toggle(opt.value)}
          >
            <input
              type="checkbox"
              className={s.quickFilterCheckboxInput}
              checked={isSelected}
              onChange={() => toggle(opt.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <span style={{ flex: 1 }}>{opt.label}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={isModal ? s.quickFiltersModalFilterRow : s.quickFilterItem}>
      <label
        htmlFor={`filter-${config.id}`}
        className={isModal ? s.quickFiltersModalLabel : s.quickFilterLabel}
      >
        {config.label}:
      </label>
      <button
        ref={triggerRef}
        id={`filter-${config.id}`}
        type="button"
        className={isModal ? s.quickFiltersModalMultiselectTrigger : s.quickFilterMultiselectTrigger}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{displayText}</span>
        <ChevronDown size={16} aria-hidden />
      </button>
      {createPortal(dropdown, document.body)}
    </div>
  );
}
